// src/components/Directory/DirectoryTable.jsx

import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import './DirectoryTable.css';
import API_BASE_URL from '../../config';

const orDash = val => {
  if (val === null || val === undefined || val === '' || Number(val) === 0) {
    return '—';
  }
  return val;
};

// Ключи в sessionStorage для сохранения состояния сортировки и прокрутки
const STORAGE_SORT_KEY = 'DirectoryTableSort';
const STORAGE_SCROLL_KEY = 'DirectoryTableScroll';

const DirectoryTable = ({ query, selectedRowId, onRowSelect, refreshFlag }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Инициализируем sortConfig из sessionStorage, если есть
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_SORT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          key: parsed.key,
          direction: parsed.direction || 'asc',
        };
      }
    } catch {}
    return { key: null, direction: 'asc' };
  });

  // Ссылка на контейнер с прокруткой таблицы
  const tableWrapperRef = useRef(null);

  // ────────────────────────────────────────────────────────────────────────────────
  // 1) Монтирование: загрузка данных, восстановление прокрутки
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [problemsRes, collectorsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/breake_problem/list_problems`),
          axios.get(`${API_BASE_URL}/collector/get_all_collectors`)
        ]);

        const problems = problemsRes.data;
        const collectors = collectorsRes.data;
        const collectorMap = collectors.reduce((acc, col) => {
          acc[col.id] = col.name;
          return acc;
        }, {});

        const mapped = problems.map(item => ({
          id: item.id,
          date: item.date ? new Date(item.date).toLocaleDateString('ru-RU') : null,
          site: collectorMap[item.collector_id] || null,
          reason: item.reason || null,
          comment: item.comments || null,
          status: item.status || null
        }));

        setRows(mapped);
      } catch (e) {
        console.error('Ошибка загрузки данных:', e);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Восстановление прокрутки таблицы из sessionStorage при монтировании
    try {
      const savedScroll = sessionStorage.getItem(STORAGE_SCROLL_KEY);
      if (savedScroll && tableWrapperRef.current) {
        tableWrapperRef.current.scrollTop = Number(savedScroll);
      }
    } catch {}
  }, [refreshFlag]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 2) Фильтрация по query
  const filteredRows = useMemo(() => {
    if (loading) return [];
    const q = (query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.date, r.site, r.reason, r.comment, r.status]
        .some(val => String(val || '').toLowerCase().includes(q))
    );
  }, [rows, query, loading]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 3) Сортировка по выбранному ключу
  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;
    const sorted = [...filteredRows].sort((a, b) => {
      let aV = a[sortConfig.key], bV = b[sortConfig.key];

      // для даты сравним timestamp
      if (sortConfig.key === 'date') {
        aV = aV ? new Date(aV).getTime() : 0;
        bV = bV ? new Date(bV).getTime() : 0;
      } else {
        aV = String(aV || '').toLowerCase();
        bV = String(bV || '').toLowerCase();
      }

      if (aV < bV) return -1;
      if (aV > bV) return 1;
      return 0;
    });
    return sortConfig.direction === 'asc' ? sorted : sorted.reverse();
  }, [filteredRows, sortConfig]);

  const handleSort = key => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  // Сохраняем состояние сортировки в sessionStorage при его изменении
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_SORT_KEY, JSON.stringify(sortConfig));
    } catch {}
  }, [sortConfig]);

  // ────────────────────────────────────────────────────────────────────────────────
  // 4) Восстановление прокрутки таблицы при изменении видимого списка (useLayoutEffect)
  useLayoutEffect(() => {
    try {
      const savedScroll = sessionStorage.getItem(STORAGE_SCROLL_KEY);
      if (savedScroll && tableWrapperRef.current) {
        tableWrapperRef.current.scrollTop = Number(savedScroll);
      }
    } catch {}
  }, [sortedRows]);

  // Обработчик прокрутки: сохраняем текущую позицию
  const handleScroll = () => {
    if (tableWrapperRef.current) {
      try {
        sessionStorage.setItem(
          STORAGE_SCROLL_KEY,
          tableWrapperRef.current.scrollTop.toString()
        );
      } catch {}
    }
  };

  // Обработчик клика по строке: сохраняем прокрутку перед навигацией
  const handleRowClick = row => {
    if (tableWrapperRef.current) {
      try {
        sessionStorage.setItem(
          STORAGE_SCROLL_KEY,
          tableWrapperRef.current.scrollTop.toString()
        );
      } catch {}
    }
    onRowSelect(row);
  };

  // ────────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="table-container">Загрузка данных...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="table-container">
      <div
        className="table-wrapper-journal"
        ref={tableWrapperRef}
        onScroll={handleScroll}
      >
        <table className="custom-table">
          <colgroup>
            <col style={{ width: '14%' }} /> {/* Дата обнаружения */}
            <col style={{ width: '20%' }} /> {/* Участок */}
            <col style={{ width: '18%' }} /> {/* Причина */}
            <col style={{ minWidth: '200px' }} /> {/* Комментарий */}
            <col style={{ width: '12%' }} /> {/* Статус */}
          </colgroup>
          <thead>
            <tr>
              <th
                className="date-col sortable-header"
                onClick={() => handleSort('date')}
              >
                Дата обнаружения
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('site')}
              >
                Участок
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('reason')}
              >
                Причина
              </th>
              <th
                className="comment-col sortable-header"
                onClick={() => handleSort('comment')}
              >
                Комментарий
              </th>
              <th
                className="sortable-header"
                onClick={() => handleSort('status')}
              >
                Статус
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(row => {
              const displayDate = row.date || '—';
              const displaySite = row.site || '—';
              const displayReason = row.reason || '—';
              const displayComment = row.comment || '—';
              const displayStatus = row.status || '—';

              return (
                <tr
                  key={row.id}
                  className={`clickable-row${row.id === selectedRowId ? ' selected-row' : ''}`}
                  onClick={() => handleRowClick(row)}
                >
                  <td className="date-col">{orDash(displayDate)}</td>
                  <td>{orDash(displaySite)}</td>
                  <td>{orDash(displayReason)}</td>
                  <td className="comment-col">{orDash(displayComment)}</td>
                  <td>
                    <span style={{
                      color:
                        displayStatus === 'Отработано' ? '#3cc962'
                        : displayStatus === 'В процессе' ? '#e6dd43'
                        : 'inherit'
                    }}>
                      {orDash(displayStatus)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DirectoryTable;
